import type { ValueGetterParams } from 'ag-grid-community';
import { get, take, zip } from 'lodash-es';
import { getMultiMarketDataSnapshotRequestKey } from '../../../../hooks/useMultiMarketDataSnapshot';
import type { MarketDataSnapshot, MarketDataSnapshotLevel } from '../../../../types';
import type { Column } from '../types';
import type { SecurityMarketDataCellValue, SecurityMarketDataColumnParams } from './types';

export type SecurityMarketDataRowReadParams = {
  marketDataCollectionPath?: string;
};

export function tryReadSecurityMarketDataRow<TData, TParams extends SecurityMarketDataRowReadParams>(
  column: Column<TParams>,
  node: ValueGetterParams['node']
):
  | {
      data: TData;
      symbol: string;
      marketDataCollectionPath: string;
    }
  | undefined {
  const data = node?.data as TData | undefined;
  const symbolField = column.field;
  const marketDataCollectionPath = column.params?.marketDataCollectionPath;
  if (!data || !symbolField || !marketDataCollectionPath) {
    return undefined;
  }

  const symbol = get(data, symbolField);
  if (typeof symbol !== 'string' || symbol.length === 0) {
    return undefined;
  }

  return {
    data,
    symbol,
    marketDataCollectionPath,
  };
}

export function getMarketDataSnapshotForRequestKey(
  data: unknown,
  marketDataCollectionPath: string,
  requestKey: string
): MarketDataSnapshot | undefined {
  return get(data, [marketDataCollectionPath, requestKey]);
}

function normalizeSizeBucket(sizeBucket: string) {
  const numericSizeBucket = Number(sizeBucket);
  return Number.isFinite(numericSizeBucket) ? numericSizeBucket.toString() : sizeBucket;
}

export function getMarketDataSnapshotSizeBucketLevel(
  data: unknown,
  snapshot: MarketDataSnapshot | undefined,
  sizeBucketField: string | undefined
): { bid?: MarketDataSnapshotLevel; offer?: MarketDataSnapshotLevel } | undefined {
  if (!snapshot || !sizeBucketField) {
    return undefined;
  }

  const sizeBucket = get(data, sizeBucketField);
  if (typeof sizeBucket !== 'string' || sizeBucket.length === 0) {
    return undefined;
  }

  const normalizedSizeBucket = normalizeSizeBucket(sizeBucket);
  const bid = snapshot.Bids?.find(bidLevel => normalizeSizeBucket(bidLevel.Size ?? '') === normalizedSizeBucket);
  const offer = snapshot.Offers?.find(
    offerLevel => normalizeSizeBucket(offerLevel.Size ?? '') === normalizedSizeBucket
  );
  return bid || offer ? { bid, offer } : undefined;
}

export function toSecurityMarketDataCellValue(
  snapshot: MarketDataSnapshot | undefined,
  sizeBucketLevel?: { bid?: MarketDataSnapshotLevel; offer?: MarketDataSnapshotLevel }
): SecurityMarketDataCellValue {
  if (!snapshot) {
    return undefined;
  }
  return { marketData: snapshot, ...sizeBucketLevel };
}

export function getSecurityMarketDataCellValue<TData>(
  column: Column<SecurityMarketDataColumnParams<TData>>,
  node: ValueGetterParams['node']
): SecurityMarketDataCellValue {
  const row = tryReadSecurityMarketDataRow<TData, SecurityMarketDataColumnParams<TData>>(column, node);
  if (!row) {
    return undefined;
  }

  const { data, symbol, marketDataCollectionPath } = row;
  const markets = Array.isArray(column.params?.Markets) ? column.params.Markets : undefined;
  const requestParams = column.params?.getRequestParams?.({
    data,
    symbol,
  });
  const sizeBuckets = requestParams?.SizeBuckets;
  const requestKey = getMultiMarketDataSnapshotRequestKey({
    Symbol: symbol,
    Markets: markets,
    SizeBuckets: sizeBuckets,
    Depth: requestParams?.Depth,
    PriceIncrement: requestParams?.PriceIncrement,
  });
  const snapshot = getMarketDataSnapshotForRequestKey(data, marketDataCollectionPath, requestKey);
  const sizeBucketLevel = getMarketDataSnapshotSizeBucketLevel(data, snapshot, column.params?.sizeBucketField);
  if (column.params?.sizeBucketField && !sizeBucketLevel) {
    return undefined;
  }
  return toSecurityMarketDataCellValue(snapshot, sizeBucketLevel);
}

export function getSecurityMarketDataBidsAndOffers(
  value: SecurityMarketDataCellValue | null | undefined
): [MarketDataSnapshotLevel | undefined, MarketDataSnapshotLevel | undefined][] {
  const marketData = value?.marketData;
  if (value?.bid || value?.offer) {
    return [[value.bid, value.offer]];
  }

  const pairs = take(zip(marketData?.Bids, marketData?.Offers), 1) as [
    MarketDataSnapshotLevel | undefined,
    MarketDataSnapshotLevel | undefined,
  ][];
  return pairs.length > 0 ? pairs : [[undefined, undefined]];
}
